import * as React from "react";
import { Text } from "@uikit";
import Layout from "@components/layout";
import { SEO } from "@components";
import { HeadFC, graphql } from "gatsby";
import HeroImages from "@components/common/heroimages";
import { useIsVisible } from "@hooks/useIsVisible";
import HotelTitle from "@components/hotel/hotelTitle";
import LocationMapAddress from "@components/common/locationMapAddress";
import HotelFacilities from "@components/hotel/hotelFacilities";
import NearbyAttractions from "@components/common/nearbyAttractions";
import NearbyAreas from "@components/common/nearbyAreas";
import {
  calcCrow,
  Hotel,
  HotelExtended,
  Hotel_Package,
  Hotel_Package_Course,
} from "@kalabo/golfsavers-common";
import {
  getPackageRoomCostForDate,
  getPackageTotalCostForDate,
  slugify,
} from "@kalabo/golfsavers-common";

import { isRegionalSite, UpsertRecentlyViewed } from "@utilities/core";
import HotelPackageItem from "@components/hotel/hotelPackageItem";
import InfoModal from "@components/hotel/infoModal";
import Pagination from "@components/common/pagination";
import SearchParams from "@components/hotel/search/searchParams";
import SearchSortBar from "@components/packages/search/searchSortBar";
import _ from "lodash";
import NearbyCourses from "@components/common/nearbyCourses";
import { useApp } from "@store/app";
import { useWindow } from "@hooks/useWindow";
import Seo from "@components/seo";
import { Course } from "@kalabo/golfsavers-common";
import { STORAGE_HOTEL_KEY } from "@utilities/constants";
import OtherHotels from "@components/hotel/otherHotels";
import Skeleton from "react-loading-skeleton";

interface IProps {
  data: any;
  pageContext: pageContextProp;
}

interface pageContextProp {
  id: string;
  slug: string;
  hotel_id: string;
  region_id: string;
}

const HotelTemplate = ({ data, pageContext }: IProps) => {
  const [initialLoad, setInitialLoad] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { slug } = pageContext;

  const win = useWindow();
  const number_of_nights = useApp(($) => $.number_of_nights);
  const room_type = useApp(($) => $.room_type);
  const package_date = useApp(($) => $.package_date);
  const search = useApp(($) => $.search);
  const display_currency = useApp(($) => $.display_currency);
  const setShowCurrencyPicker = useApp(($) => $.setShowCurrencyPicker);

  const imageRef = React.useRef<any>();
  const infoModalRef = React.useRef<any>();

  const golfPackagesLink = React.useRef<any>();
  const aboutJumpLink = React.useRef<any>();
  const hotelAmenitiesJumpLink = React.useRef<any>();
  const locationJumpLink = React.useRef<any>();
  const nearbyCoursesJumpLink = React.useRef<any>();

  const isImageVisible = useIsVisible(imageRef);

  const hotel = React.useMemo(() => {
    return data.allGolfsaversHotels.edges[0].node as Hotel;
  }, [data]);

  React.useEffect(() => {
    UpsertRecentlyViewed(STORAGE_HOTEL_KEY, hotel.hotel_id);
  }, [hotel]);

  React.useEffect(() => {
    setInitialLoad(false);
  }, []);

  React.useEffect(() => {
    if (initialLoad) return;

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [number_of_nights, room_type, package_date]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [recordsPerPage] = React.useState(5);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  let current_region = data.regions.edges?.find(
    (region: any) =>
      region.node.region_id ===
      (process.env.GATSBY_REGION_ID || pageContext?.region_id)
  ).node;

  const courses = data.allGolfsaversGolfCourses.edges.map((a: any) => {
    return a.node;
  });

  const region_hotels = data.region_hotels.edges.map((a: any) => {
    return a.node;
  });
  const airports = data.airports.edges.map((a: any) => {
    return a.node;
  });

  const filteredPackages = React.useMemo(() => {
    let packages = _.cloneDeep(hotel.packages);

    // does the room_type even exist in the list of packages?
    const room_types = [...new Set(packages?.map((p) => p.room.room_type))];
    let room_exists = true;

    if (room_type && !room_types.includes(room_type)) {
      room_exists = false;
    }

    if (number_of_nights) {
      packages = packages.filter((pkg: Hotel_Package) => {
        return pkg.number_of_nights === number_of_nights;
      });
    }

    if (room_type && room_exists) {
      packages = packages.filter((pkg: Hotel_Package) => {
        return pkg.room.room_type === room_type;
      });
    }

    if (search?.hotel?.filters?.courses?.length > 0) {
      console.log(
        "search?.hotel?.filters?.course",
        search?.hotel?.filters?.courses
      );
      packages = packages.filter((pkg: Hotel_Package) => {
        return pkg.courses.some((course: Hotel_Package_Course) => {
          return search?.hotel?.filters?.courses?.some((filter: any) => {
            return filter.value === course.id;
          });
        });
      });
    }

    if (search?.package?.sort) {
      if (
        search?.package?.sort?.field === "price_asc" ||
        search?.package?.sort?.field === "price_desc"
      ) {
        packages = packages.sort((a: Hotel_Package, b: Hotel_Package) => {
          if (search?.package?.sort?.field === "price_asc") {
            return (
              getPackageTotalCostForDate(package_date, a) -
              getPackageTotalCostForDate(package_date, b)
            );
          } else {
            return (
              getPackageTotalCostForDate(package_date, a) +
              getPackageTotalCostForDate(package_date, b)
            );
          }
        });
      }
      if (search?.package?.sort?.field === "room") {
        packages = packages.sort((a: Hotel_Package, b: Hotel_Package) => {
          return (
            getPackageRoomCostForDate(package_date, a) +
            getPackageRoomCostForDate(package_date, b)
          );
        });
      }
    }

    return packages as Hotel_Package[];
  }, [
    number_of_nights,
    search?.package,
    search?.hotel?.filters?.courses,
    hotel.packages,
    room_type,
    indexOfFirstRecord,
    indexOfLastRecord,
  ]);

  const pagedPackages = React.useMemo(() => {
    return filteredPackages.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [filteredPackages, indexOfFirstRecord, indexOfLastRecord]);

  const nPages = React.useMemo(() => {
    return Math.ceil(filteredPackages.length / recordsPerPage);
  }, [filteredPackages, recordsPerPage]);

  let breadcrumb = React.useMemo(() => {
    if (isRegionalSite()) {
      return [
        { name: "Home", url: "/" },
        {
          name: `${current_region.name.en} Golf Packages`,
          url: `/${slugify(current_region.slug)}-golf-packages`,
        },
        { name: hotel.name.en, url: "" },
      ];
    } else {
      return [
        { name: "Home", url: "/" },
        {
          name: current_region.country.name.en,
          url: `/${slugify(current_region.country.slug)}`,
        },
        {
          name: `${current_region.name.en} Golf Packages`,
          url: `/${slugify(
            current_region.country.slug
          )}-golf-packages/${slugify(current_region.slug)}-golf-holidays`,
        },
        { name: hotel.name.en, url: "" },
      ];
    }
  }, []);

  let nearby_hotels = React.useMemo(() => {
    if (!hotel.nearby_hotels) return [];
    if (!region_hotels) return [];

    let ids = hotel.nearby_hotels.map((h: any) => {
      return h.id;
    });

    return region_hotels
      .filter((h: any) => {
        return ids.includes(h.hotel_id);
      })
      .filter((hotel: Hotel) => {
        let packages = hotel?.packages;
        if (!packages) return false;

        let packages_with_rate = packages.filter((pkg: any) => {
          return getPackageTotalCostForDate(package_date, pkg) > 0;
        });
        if (packages_with_rate.length < 1) return false;
        return true;
      });
  }, [region_hotels, hotel]);

  if (!hotel) return null;

  return (
    <>
      <Layout
        pageTitle={hotel?.seo?.title}
        keywords={hotel?.seo?.keywords}
        description={hotel?.seo?.description}
        canonical={hotel?.seo?.canonical}
        fullWidth
        breadcrumb={breadcrumb}
        className="mb-20"
      >
        <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
          <div className="flex flex-col">
            <HotelTitle
              hotel={hotel}
              region={hotel?.region}
              country={hotel?.region?.country}
              onShowMap={() => {
                infoModalRef.current && infoModalRef.current.open(2);
              }}
            />
            <div className="mb-0 sm:mb-10">
              <HeroImages
                course={hotel}
                onClick={() => {
                  infoModalRef.current && infoModalRef.current.open(1);
                }}
                images={hotel.images}
                recommended={hotel.is_recommended}
              />
            </div>
            <div ref={imageRef}></div>
          </div>{" "}
        </div>
        <div
          className={`sticky  sm:mx-0 sm:fixed sm:left-0 sm:right-0 top-[-1px] z-[900] ${
            isImageVisible ? "sm:hidden" : "sm:block"
          }`}
        >
          <div className="bg-white border-b border-grey3 items-center sm:mb-10 flex">
            <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-5 sm:px-10 lg:px-0 min-h-100 flex w-full overflow-auto noscrollbar">
              <div className="flex gap-[30px] sm:gap-10">
                <div
                  className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer order-1"
                  onClick={() => {
                    if (win) {
                      win.scrollTo({
                        top: golfPackagesLink.current.offsetTop - 170,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  <Text
                    size={15}
                    weight="semibold"
                    color={"black90"}
                    className="whitespace-nowrap mt-[5px]"
                  >
                    Golf Packages
                  </Text>
                </div>
                <div
                  className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer order-2"
                  onClick={() => {
                    if (win) {
                      win.scrollTo({
                        top: aboutJumpLink.current.offsetTop - 100,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  <Text
                    size={15}
                    weight="semibold"
                    color={"black90"}
                    className="mt-[5px]"
                  >
                    About
                  </Text>
                </div>
                <div
                  className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer order-3 lg:order-4"
                  onClick={() => {
                    if (win) {
                      win.scrollTo({
                        top: hotelAmenitiesJumpLink.current.offsetTop - 20,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  <Text
                    size={15}
                    weight="semibold"
                    color={"black90"}
                    className="mt-[5px]"
                  >
                    Amenities
                  </Text>
                </div>
                <div
                  className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer order-4 lg:order-3"
                  onClick={() => {
                    if (win) {
                      win.scrollTo({
                        top: locationJumpLink.current.offsetTop - 100,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  <Text
                    size={15}
                    weight="semibold"
                    color={"black90"}
                    className="mt-[5px]"
                  >
                    Location
                  </Text>
                </div>
                <div
                  className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer order-5"
                  onClick={() => {
                    if (win) {
                      win.scrollTo({
                        top: nearbyCoursesJumpLink.current.offsetTop - 100,
                        behavior: "smooth",
                      });
                    }
                  }}
                >
                  <Text
                    size={15}
                    weight="semibold"
                    color={"black90"}
                    className="mt-[5px] whitespace-nowrap"
                  >
                    Nearby Courses
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
        <>
          <div
            className="items-center flex mt-10 sm:mt-0"
            style={{
              background:
                "linear-gradient(180deg, rgb(255 255 255) 50%, rgb(245 247 250) 50%)",
            }}
          >
            <a id="packages" ref={golfPackagesLink} />
            <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
              <div className="flex justify-between">
                <Text
                  size={21}
                  element="h2"
                  color="black"
                  weight="semibold"
                  leading={1.3}
                >
                  Golf Packages
                </Text>
                <div className="gap-2 hidden sm:flex">
                  <Text size={15} color="black90">
                    Showing prices in{" "}
                    <span
                      onClick={() => {
                        setShowCurrencyPicker();
                      }}
                      className="font-semibold text-15 underline cursor-pointer hover:text-blue"
                    >
                      {display_currency.code}
                    </span>
                  </Text>
                </div>
              </div>
              <div>
                <SearchParams packages={hotel.packages} />
                <SearchSortBar
                  package_count={filteredPackages.length}
                  hotel={hotel}
                />
              </div>
            </div>
          </div>
          <div className="bg-grey items-center mb-10 lg:mb-[60px] flex">
            <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
              {isLoading ? (
                <>
                  {_.times(5, (i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 sm:gap-5 mb-5 border border-grey2  items-center"
                    >
                      <div className="col-span-12 sm:col-span-12 lg:col-span-12">
                        <Skeleton count={1} height={200} />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex flex-col gap-5">
                  {filteredPackages.length === 0 ? (
                    <div className="bg-[#FDF6EC] rounded-md border border-grey3 px-4 py-10 mb-10 lg:mb-[60px] flex flex-col items-center justify-center">
                      <Text
                        size={17}
                        weight="semibold"
                        leading={1.3}
                        color="black90"
                        className=" text-center mb-3"
                      >
                        No golf packages fit your search criteria
                      </Text>
                      <Text
                        size={15}
                        leading={1.5}
                        element="p"
                        color="black90"
                        className="text-center"
                      >
                        Try adjusting your arrival date or the number or nights
                        to see results
                      </Text>
                    </div>
                  ) : (
                    <>
                      {pagedPackages.map((pkg: Hotel_Package, idx: any) => {
                        return (
                          <HotelPackageItem
                            region={current_region}
                            package={pkg}
                            key={idx}
                            hotel_slug={slug}
                          />
                        );
                      })}
                    </>
                  )}
                </div>
              )}
              {filteredPackages.length > 0 && (
                <div className="mt-6 lg:mt-8 mb-10 lg:mb-[60px]">
                  <Pagination
                    mode="simple"
                    handlePagination={setCurrentPage}
                    page={currentPage}
                    pageSize={recordsPerPage}
                    totalItems={filteredPackages.length}
                    totalPages={nPages}
                  />
                </div>
              )}
            </div>
          </div>
        </>
        <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
          <a id="about" ref={aboutJumpLink} />
          <div className="grid grid-cols-12 lg:gap-x-[60px]">
            <div className="col-span-12 sm:col-span-12 lg:col-span-6 ">
              <Text
                size={21}
                color="black"
                weight="semibold"
                element="h2"
                leading={1.3}
                className="mb-[25px] lg:mb-[30px]"
              >
                About {hotel.name.en}
              </Text>
              <Text
                size={17}
                color="black80"
                leading={1.5}
                element="h3"
                weight="semibold"
                className="mb-[10px]"
              >
                {hotel.summary.en}
              </Text>
              <Text
                size={15}
                leading={1.5}
                element="p"
                color="black80"
                dangerouslySetInnerHTML={{ __html: hotel.description.en }}
              />
              <a id="amenities" ref={hotelAmenitiesJumpLink} />
              <HotelFacilities facilities={hotel.facilities} />
            </div>

            <div className="col-span-12 lg:col-span-6">
              <a id="location" ref={locationJumpLink} />

              <div className=" border-t pt-10 lg:border-none lg:pt-0  border-grey3 ">
                <LocationMapAddress
                  onClick={() => {
                    infoModalRef.current && infoModalRef.current.open(2);
                  }}
                  title="Location"
                  description={""}
                  address={hotel.full_address}
                  longitude={hotel.longitude}
                  latitude={hotel.latitude}
                />
                <NearbyAreas
                  title="Nearby Airports"
                  types={["Airport"]}
                  maxDistance={50}
                  center={{
                    latitude: hotel.latitude,
                    longitude: hotel.longitude,
                  }}
                />
                <NearbyAttractions
                  center={{
                    latitude: hotel.latitude,
                    longitude: hotel.longitude,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="border-t border-grey3 pt-10">
            <a id="nearbyCoursesJumpLink" ref={nearbyCoursesJumpLink} />
            <Text
              size={21}
              color="black"
              weight="semibold"
              leading={1.3}
              element="h2"
              className="mb-[25px] lg:mb-[30px]"
            >
              Golf Courses Nearby
            </Text>
            <div className=" pb-[60px]">
              <div className="grid grid-cols-1 sm:grid-cols-[_repeat(2,_minmax(0,_1fr))] sm:grid-rows-[1fr,1fr,1fr] gap-y-3 gap-x-10 sm:grid-flow-col  ">
                {" "}
                <NearbyCourses
                  courses={hotel.nearby_courses}
                  region={current_region}
                  listMode
                  mode="large"
                  fromArea="hotel"
                  center={{
                    latitude: hotel.latitude,
                    longitude: hotel.longitude,
                  }}
                  limit={6}
                  maxDistance={100}
                />
              </div>
            </div>
          </div>
        </div>
        <OtherHotels
          hotels={nearby_hotels}
          region={current_region}
          center={{
            latitude: hotel.latitude,
            longitude: hotel.longitude,
          }}
          maxDistance={100}
        />
        <InfoModal
          ref={infoModalRef}
          courses={courses}
          airports={airports}
          longitude={hotel.longitude}
          latitude={hotel.latitude}
          images={hotel.images}
        />
      </Layout>
    </>
  );
};

export default HotelTemplate;

export const Head = ({ data }: IProps) => {
  const hotel = React.useMemo(() => {
    return data.allGolfsaversHotels.edges[0].node;
  }, [data]);

  let year = new Date().getFullYear();

  return (
    <Seo
      title={`Book ${hotel.name.en} | Best ${hotel.region.name.en} Golf Deals ${year}`}
      description={`Combine a stay at the ${hotel.star_rating}-star ${hotel.name.en} with rounds of golf at the best golf courses in ${hotel.region.name.en}. Get the best deals on golf packages with ${process.env.GATSBY_SITE_URL}.
      `}
      keywords={``}
      canonical={``}
    />
  );
};

export const query = graphql`
  query ($id: String, $country_id: String!, $region_id: String!) {
    airports: allGolfsaversAreas(
      filter: { type: { eq: "Airport" }, region_id: { eq: $region_id } }
    ) {
      edges {
        node {
          latitude
          longitude
          name {
            en
          }
          type
        }
      }
    }
    allGolfsaversGolfCourses(
      filter: { published: { eq: true }, region_id: { eq: $region_id } }
    ) {
      edges {
        node {
          id
          name {
            en
          }
          course_id
          longitude
          latitude
          reviews {
            count
            average_rating
          }
        }
      }
    }
    region_hotels: allGolfsaversHotels(
      filter: { published: { eq: true }, region_id: { eq: $region_id } }
    ) {
      edges {
        node {
          id
          name {
            en
          }
          region_id
          area {
            id
            name {
              en
            }
          }
          packages {
            name
            course_combo
            currency_code
            group_size
            number_of_nights
            slug
            room {
              board_type
              room_type
              room_basis
              prices {
                cost
                from_date
                margin
                to_date
                total
              }
            }
            courses {
              id
              prices {
                green_fees {
                  from_date
                  sell_price
                  to_date
                }
                transfer_price
              }
              slug
            }
          }
          images {
            alt
            url
          }
          published
          slug
          star_rating
          region_id
          summary {
            en
          }
          hotel_id
        }
      }
    }
    regions: allGolfsaversRegions(filter: { country_id: { eq: $country_id } }) {
      edges {
        node {
          id
          top_courses
          region_id
          name {
            en
          }
          slug
          country {
            id
            name {
              en
            }
            slug
          }
          course_listing_image
          package_listing_image
        }
      }
    }
    allGolfsaversHotels(filter: { hotel_id: { eq: $id } }) {
      edges {
        node {
          id
          nearby_hotels {
            id
            distance {
              km
              miles
            }
          }
          nearby_courses {
            id
            course_id
            slug
            region_id
            longitude
            latitude
            featured_image {
              alt
              url
            }
            is_recommended
            name {
              en
            }
            reviews {
              count
              average_rating
              reviews {
                id
                overall
              }
            }
            summary {
              en
            }
            course_type
            price_range
            location_description {
              en
            }
            days_of_operation {
              fri
              hol
              mon
              sat
              sun
              thu
              tue
              wed
            }
          }
          name {
            en
          }
          description {
            en
          }
          area {
            id
            name {
              en
            }
          }
          packages {
            name
            airport_area_id
            airport_area {
              id
              name {
                en
              }
            }
            airport_return_transfer_price
            airport_transfer
            course_combo
            course_transfer
            golf_transfer_discount
            airport_transfer_discount
            golf_discount
            currency_code
            group_size
            number_of_nights
            slug
            room {
              board_type
              room_type
              room_basis
              image {
                alt
                url
              }
              prices {
                cost
                from_date
                margin
                to_date
                total
              }
            }
            courses {
              id
              name {
                en
              }
              prices {
                green_fees {
                  from_date
                  sell_price
                  to_date
                }
                transfer_price
              }
              reviews {
                count
                average_rating
              }
            }
          }
          facilities {
            bar_lounge
            breakfast_buffet
            business_center
            fitness_center
            free_wifi
            indoor_pool
            kids_club
            non_smoking_rooms
            onsite_golf
            outdoor_pool
            pets_allowed
            restaurant
            room_service
            spa
            tennis_courts
          }
          full_address
          hotel_type
          images {
            alt
            url
          }
          is_recommended
          latitude
          longitude
          number_of_rooms
          policies {
            checkin_time
            checkout_time
          }
          published
          region {
            country {
              id
              name {
                en
              }
              slug
            }
            slug
            id
            name {
              en
            }
          }
          slug
          star_rating
          seo {
            canonical
            description
            keywords
            title
          }
          region_id
          summary {
            en
          }
          hotel_id
        }
      }
    }
  }
`;
